import type {
  RepositoryIntelligenceQuestion,
  RepositoryIntelligenceQuestionList,
  RepositoryIntelligenceQuestionReceipt,
  RepositoryIntelligenceQuestionRequest,
} from '@ashniva/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../../shared/lib/api-client';
import { intelligencePath, riKeys, riMutationRetry, riRetry, type Submission } from './api';
import { usePollWindow } from './use-poll-window';

/** Questions about a repository's code: asked, followed until answered, and the person's own earlier ones. */

const questionKey = (linkId: string, questionId: string) =>
  [...riKeys.link(linkId), 'question', questionId] as const;

export function useAskQuestion(linkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RepositoryIntelligenceQuestionRequest & Submission) =>
      apiRequest<RepositoryIntelligenceQuestionReceipt>(`${intelligencePath(linkId)}/questions`, {
        method: 'POST',
        body: { ...input, mode: 'evidence' },
      }),
    retry: riMutationRetry,
    onSuccess: (receipt) => {
      // The receipt is the question as it stands: the screen shows it at once and polls from there.
      const { created: _created, ...question } = receipt;
      client.setQueryData(questionKey(linkId, receipt.question.id), question);
      void client.invalidateQueries({ queryKey: [...riKeys.link(linkId), 'questions'] });
    },
  });
}

const pollAfterOfQuestion = (data: RepositoryIntelligenceQuestion | undefined) =>
  data ? data.question.pollAfterMs : undefined;

/** One of the person's questions, polled while it is answered (`pollAfterMs`, with back-off, for two minutes). */
export function useQuestionQuery(linkId: string, questionId: string | null) {
  // The window's clock runs from when the question is opened; once the answer is final the polls stop by themselves
  // (`pollAfterMs` is null) and "still working" is never shown.
  const poll = usePollWindow(questionId, Boolean(questionId), pollAfterOfQuestion);
  const query = useQuery({
    queryKey: questionKey(linkId, questionId ?? ''),
    queryFn: () =>
      apiRequest<RepositoryIntelligenceQuestion>(
        `${intelligencePath(linkId)}/questions/${encodeURIComponent(questionId ?? '')}`,
      ),
    enabled: Boolean(questionId),
    retry: riRetry,
    refetchInterval: poll.refetchInterval,
    refetchIntervalInBackground: true,
  });
  const final = query.data ? query.data.question.pollAfterMs === null : false;
  return {
    query,
    stillWorking: poll.stillWorking && !final,
    checkAgain: () => {
      poll.restart();
      void query.refetch();
    },
  };
}

export function useQuestionsQuery(linkId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...riKeys.link(linkId), 'questions'],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceQuestionList>(`${intelligencePath(linkId)}/questions`, {
        query: { limit: 20 },
      }),
    enabled,
    retry: riRetry,
  });
}
